import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useCreateUser, useUpdateUser } from "@/hooks/mutations/users";
import { useUser } from "@/hooks/queries/useUsers";
import { useRoles } from "@/hooks/queries/useRoles";
import { toast } from "sonner"; 
import { userService } from "@/services/userService";
import Select from "react-select";
import useAuth from "@/hooks/stores/useAuth";


const UserForm = () => {
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { userId: authUserId, userRole: authUserRole } = useAuth();
  const isEditMode = !!userId;
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisors, setSelectedAdvisors] = useState([]);
  const [headsOfDepartment, setHeadsOfDepartment] = useState([]);
  const [userRole, setUserRole] = useState("");
  const isAuthUserHOD = authUserRole?.toLowerCase() === "head of department";

  // Queries and Mutations
  const { data: user, isLoading: isLoadingUser } = useUser(userId, {
    enabled: isEditMode,
  });
  const { data: rolesData, isLoading: isLoadingRoles } = useRoles();
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();

  const roles = rolesData?.data || rolesData || [];
  const preselectedRoleTitle = location.state?.role;
  const preselectedRoleId = roles.find((role) => role.title === preselectedRoleTitle)?.id;

  // Form Validation Schema
  const validationSchema = Yup.object({
    name: Yup.string().min(3, "Name must be at least 3 characters").required("Name is required"),
    email: Yup.string()
      .email("Invalid email format")
      .required("Email is required"),
    password: isEditMode ? Yup.string() : Yup.string().required("Password is required"),
    roles: Yup.array().min(1, "At least one role must be selected").required("Role is required"),
  });

  // Formik Setup
  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      password: "",
      roles: preselectedRoleId ? [preselectedRoleId] : [],
      headOfDepartmentId: isAuthUserHOD ? authUserId : null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        const dataToSubmit = { ...values };

        if (userRole.toLowerCase() === "head of department") {
          dataToSubmit.legalAdvisorIds = selectedAdvisors.map((advisor) => advisor.value);
        } else if (userRole.toLowerCase() === "legal advisor") {
          dataToSubmit.assigned_to = isAuthUserHOD ? authUserId : values.headOfDepartmentId;
        }

        if (isEditMode) {
          await updateUser.mutateAsync({ id: userId, data: dataToSubmit });
          toast.success("User updated successfully!");
        } else {
          await createUser.mutateAsync(dataToSubmit);
          toast.success("User created successfully!");
        }

        // Redirect based on user role
        let redirectPath = "/Users";
        switch (userRole.toLowerCase()) {
          case "head of department":
            redirectPath = "/head_of_department";
            break;
          case "legal advisor":
            redirectPath = "/advisors";
            break;
          case "user":
            redirectPath = "/Users";
            break;
        }
        navigate(redirectPath);
      } catch (error) {
        console.error("Error response:", error.response);

        if (error.response && error.response.status === 422) {
          const errorData = error.response.data;

          if (errorData.message && errorData.message.includes("email has already been taken")) {
            toast.error("User already exists.");
          } else if (typeof errorData.errors === "object") {
            const message = Object.values(errorData.errors).flat().join(", ");
            toast.error(message);
          } else {
            toast.error(errorData.message || `Failed to ${isEditMode ? "update" : "create"} user`);
          }
        } else {
          toast.error(error.message || `Failed to ${isEditMode ? "update" : "create"} user`);
        }
      }
    },
  });

  // Set userRole when component mounts if preselectedRoleTitle exists
  useEffect(() => {
    if (preselectedRoleTitle) {
      setUserRole(preselectedRoleTitle);

      // Wait for roles to be available before setting the role ID
      if (roles.length > 0) {
        const roleId = roles.find((role) => role.title === preselectedRoleTitle)?.id;
        if (roleId) {
          formik.setFieldValue("roles", [roleId]);
        }
      }
    }
  }, [preselectedRoleTitle, roles]); // Add roles as a dependency

  // Load existing user data when in edit mode
  useEffect(() => {
    if (user) {
      formik.setValues({
        name: user.user.name || "",
        email: user.user.email || "",
        roles: user.user.roles?.map((role) => role.id) || [],
        headOfDepartmentId: user.user.assigned_to || null,
      });

      setUserRole(user.user.roles[0].title);
      setSelectedAdvisors(user.user.legalAdvisorIds || []);
    } else {
      setUserRole(preselectedRoleTitle);
    }
  }, [user]);

  // Fetch advisors and heads of department
  const fetchAdvisors = async () => {
    try {
      const response = await userService.getlegalAdvisorsUnassigned();
      if (response.success && Array.isArray(response.data)) {
        setAdvisors(response.data);
      } else {
        setAdvisors([]);
      }
    } catch (error) {
      console.error("Failed to fetch advisors:", error);
    }
  };

  const fetchHeadsOfDepartment = async () => {
    try {
      const response = await userService.getheadOfDepartment();
      if (response.success && response.data?.users) {
        setHeadsOfDepartment(response.data.users);
      } else {
        setHeadsOfDepartment([]);
      }
    } catch (error) {
      console.error("Failed to fetch heads of department:", error);
    }
  };

  useEffect(() => {
    if (userRole) {
      if (userRole.toLowerCase() === "head of department") {
        fetchAdvisors();
      } else if (userRole.toLowerCase() === "legal advisor") {
        fetchHeadsOfDepartment();
      }
    }
  }, [userRole]);

  // Transform advisors for react-select
  const advisorOptions = advisors.map((advisor) => ({
    value: advisor.id,
    label: advisor.name,
  }));

  // Transform heads of department for select
  const headOfDepartmentOptions = headsOfDepartment.map((head) => ({
    value: head.id,
    label: head.name,
  }));

  if (isEditMode && isLoadingUser)
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  if (isLoadingRoles) 
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#F16745]"></div>
      </div>
    );

  return (
    <div className="p-6 lg:p-8">
      {/* Add Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="px-1 sm:px-2 flex items-center text-[#F16745] hover:text-[#F16745]/80"
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-5 w-5 mr-2" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M10 19l-7-7m0 0l7-7m-7 7h18" 
          />
        </svg>
        Back
      </button>

      <div className="bg-white dark:bg-dark-2 p-8 rounded-lg shadow-card">
        <h2 className="text-2xl font-semibold mb-6 text-black dark:text-[#F16745]">
          {isEditMode ? "Edit User" : (
            preselectedRoleTitle === "Head of Department" ? "Create New HOD" :
            preselectedRoleTitle === "Legal Advisor" ? "Create New Advisor" :
            "Create New User"
          )}
        </h2>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div>
              <label className="block font-medium mb-2 text-black">Name</label>
              <input
                type="text"
                name="name"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter name"
                className="w-full rounded-md bg-transparent px-5 py-3 text-body-color  focus:ring-0  focus:border-[#F16745] dark:border-dark-3 dark:text-dark-6"
              />
              {formik.touched.name && formik.errors.name && (
                <p className="text-red-500">{formik.errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label className="block font-medium mb-2 text-black">Email</label>
              <input
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter email"
                className="w-full rounded-md border border-stroke bg-transparent px-5 py-3 text-body-color outline-none focus:ring-0 focus:border-[#F16745] dark:border-dark-3 dark:text-dark-6"
              />
              {formik.touched.email && formik.errors.email && (
                <p className="text-red-500">{formik.errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            {!isEditMode && (
              <div>
                <label className="block font-medium mb-2 text-black">Password</label>
                <input
                  type="password"
                  name="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter password"
                  className="w-full rounded-md border border-stroke bg-transparent px-5 py-3 text-body-color outline-none focus:ring-0 focus:border-[#F16745] dark:border-dark-3 dark:text-dark-6"
                />
                {formik.touched.password && formik.errors.password && (
                  <p className="text-red-500">{formik.errors.password}</p>
                )}
              </div>
            )}

            {/* Roles Dropdown */}
            <div>
              <label className="block font-medium mb-2 text-black">Roles</label>
              <select
                name="roles"
                value={formik.values.roles[0] || ""}
                onChange={(e) => {
                  const roleId = parseInt(e.target.value);
                  formik.setFieldValue("roles", [roleId]);
                  const selectedRole = roles.find((role) => role.id === roleId);
                  if (selectedRole) {
                    setUserRole(selectedRole.title);
                  }
                }}
                onBlur={formik.handleBlur}
                disabled={!!preselectedRoleTitle}
                className="w-full rounded-md border border-stroke bg-transparent px-5 py-3 text-body-color  outline-none focus:ring-0 focus:border-[#F16745] dark:border-dark-3 dark:text-dark-6"
              >
                <option value="">Select a role</option>
                {Array.isArray(roles) &&
                  roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.title}
                    </option>
                  ))}
              </select>
              {formik.touched.roles && formik.errors.roles && (
                <p className="text-red-500">{formik.errors.roles}</p>
              )}
            </div>
          </div>

          {/* Advisors Multi-Select Field */}
          {roles.find((role) => role.id === formik.values.roles[0])?.title === "Head of Department" && (
            <div className="mb-4">
              <label className="block font-medium mb-2 text-black">Select Advisors</label>
              {advisorOptions.length > 0 ? (
                <Select
                  isMulti
                  options={advisorOptions}
                  value={selectedAdvisors}
                  onChange={(selected) => {
                    setSelectedAdvisors(selected.map((option) => option));
                  }}
                  className="basic-multi-select"
                  classNamePrefix="select"
                  placeholder="Select advisors..."
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderColor: state.isFocused ? '#F16745' : baseStyles.borderColor,
                      '&:hover': {
                        borderColor: '#F16745'
                      },
                      boxShadow: state.isFocused ? '0 0 0 1px #F16745' : baseStyles.boxShadow
                    }),
                    option: (baseStyles, state) => ({
                      ...baseStyles,
                      backgroundColor: state.isFocused ? '#F16745' : baseStyles.backgroundColor,
                      color: state.isFocused ? 'white' : baseStyles.color,
                      '&:hover': {
                        backgroundColor: '#F16745',
                        color: 'white'
                      }
                    }),
                    input: (baseStyles) => ({
                      ...baseStyles,
                      '& input:focus': {
                        boxShadow: 'none',
                        outline: 'none',
                        borderColor: '#F16745'
                      }
                    }),
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                />
              ) : (
                <p className="text-[#F16745]">{formik.errors.roles}</p>
              )}
            </div>
          )}

          {/* Head of Department Select Field */}
          {roles.find((role) => role.id === formik.values.roles[0])?.title.toLowerCase() === "legal advisor" && !isAuthUserHOD && (
            <div className="mb-4">
              <label className="block font-medium mb-2 text-black">Select Head of Department</label>
              <Select
                options={headOfDepartmentOptions}
                value={headOfDepartmentOptions.find(head => head.value === formik.values.headOfDepartmentId) || null}
                onChange={(selected) => formik.setFieldValue("headOfDepartmentId", selected?.value || null)}
                className="basic-select"
                classNamePrefix="select"
                placeholder="Select a head of department..."
                styles={{
                  control: (baseStyles, state) => ({
                    ...baseStyles,
                    borderColor: state.isFocused ? '#F16745' : baseStyles.borderColor,
                    '&:hover': {
                      borderColor: '#F16745'
                    },
                    boxShadow: state.isFocused ? '0 0 0 1px #F16745' : baseStyles.boxShadow
                  }),
                  option: (baseStyles, state) => ({
                    ...baseStyles,
                    backgroundColor: state.isFocused ? '#F16745' : baseStyles.backgroundColor,
                    color: state.isFocused ? 'white' : baseStyles.color,
                    '&:hover': {
                      backgroundColor: '#F16745',
                      color: 'white'
                    }
                  }),
                  input: (baseStyles) => ({
                    ...baseStyles,
                    '& input:focus': {
                      boxShadow: 'none',
                      outline: 'none',
                      borderColor: '#F16745'
                    }
                  }),
                  menuPortal: base => ({ ...base, zIndex: 9999 })
                }}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/users")}
              className="px-6 py-3 rounded-md border border-stroke text-body-color hover:border-[#F16745] hover:text-[#F16745] dark:border-dark-3 dark:text-dark-6"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 rounded-md bg-[#F16745] text-white hover:bg-opacity-90"
              disabled={createUser.isLoading || updateUser.isLoading}
            >
              {createUser.isLoading || updateUser.isLoading ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;